import { routePaths } from 'components/Routes/data';
import { ValidationSchema } from 'forms';
import {
  CreateEmployeeFieldInput,
  EmployeeFieldAccess,
  EmployeeFieldDefinition,
  EmployeeFieldValueType,
  PayrollCustomFieldDefinition,
  PayrollCreateCustomFieldRequest,
  PayrollFieldUsage,
  PayrollUpdateCustomFieldRequest,
} from 'api/gql-utils/employeeDataSettings/types';
import { employeeDataTabs, FIELD_TYPE_OPTIONS } from './constants';
import { EmployeeFieldFormData } from './types';

export const getEmployeeDataTabValue = (pathname: string): string => {
  switch (pathname) {
    case routePaths.settings.employeeData.fields:
      return employeeDataTabs.fields;
    case routePaths.settings.employeeData.root:
    case routePaths.settings.employeeData.general:
    default:
      return employeeDataTabs.general;
  }
};

export const getFieldTypeLabel = (valueType: EmployeeFieldValueType): string =>
  FIELD_TYPE_OPTIONS.find((option) => option.value === valueType)?.label ?? valueType;

export const groupFieldsBySection = (fields: EmployeeFieldDefinition[]) =>
  fields.reduce<Record<string, EmployeeFieldDefinition[]>>((acc, field) => {
    const section = field.section || 'Default';
    acc[section] = [...(acc[section] ?? []), field];
    return acc;
  }, {});

export const getFieldKeyFromLabel = (label: string) =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, '_')
    .replace(/^_+|_+$/g, '');

const normalizeValueType = (valueType: string | null): EmployeeFieldValueType => {
  const matchedType = FIELD_TYPE_OPTIONS.find((option) => option.value === valueType)?.value;
  return matchedType ?? 'STRING';
};

const normalizeOrigin = (origin: string | null): EmployeeFieldDefinition['origin'] =>
  origin === 'PREDEFINED' ? 'PREDEFINED' : 'CUSTOM';

const normalizeEnumValues = (enumValues: PayrollCustomFieldDefinition['enumValues']): string[] => {
  if (!enumValues) return [];
  if (Array.isArray(enumValues)) return enumValues;
  return Object.values(enumValues)
    .map((option) => option?.label)
    .filter(Boolean) as string[];
};

const normalizeAccessPolicy = (
  accessPolicy: PayrollCustomFieldDefinition['accessPolicy'],
): Record<number, EmployeeFieldAccess> => {
  return Object.entries(accessPolicy ?? {}).reduce<Record<number, EmployeeFieldAccess>>(
    (acc, [roleId, access]) => {
      const parsedRoleId = Number(roleId);
      if (Number.isFinite(parsedRoleId) && access) acc[parsedRoleId] = access;
      return acc;
    },
    {},
  );
};

export const mapPayrollCustomFieldDefinition = (
  definition: PayrollCustomFieldDefinition,
): EmployeeFieldDefinition => {
  const fieldKey = definition.fieldKey ?? definition.id ?? '';
  const usages = definition.usages ?? [];

  return {
    id: definition.id ?? fieldKey,
    fieldKey,
    label: definition.label ?? fieldKey,
    valueType: normalizeValueType(definition.valueType),
    origin: normalizeOrigin(definition.origin),
    isRequired: Boolean(definition.isRequired),
    showOnPayslip: usages.includes('PAYSLIP_DISPLAY'),
    enumValues: normalizeEnumValues(definition.enumValues),
    section: definition.section || 'Default',
    displayOrder: definition.displayOrder ?? 0,
    accessPolicy: normalizeAccessPolicy(definition.accessPolicy),
  };
};

export const getEnumValuesPayload = (enumValues?: string[]) =>
  (enumValues ?? []).reduce<Record<string, { label: string; deprecated: boolean }>>(
    (acc, enumValue) => {
      acc[enumValue] = { label: enumValue, deprecated: false };
      return acc;
    },
    {},
  );

export const getFieldUsages = (input: CreateEmployeeFieldInput): PayrollFieldUsage[] => {
  const usages: PayrollFieldUsage[] = ['PAYROLL_FILTER'];
  if (input.showOnPayslip) usages.push('PAYSLIP_DISPLAY');
  return usages;
};

export const buildCreateCustomFieldVariables = (
  input: CreateEmployeeFieldInput,
  displayOrder: number,
): PayrollCreateCustomFieldRequest => ({
  input: {
    fieldKey: getFieldKeyFromLabel(input.label),
    label: input.label,
    valueType: input.valueType,
    isRequired: input.isRequired,
    enumValues: input.valueType === 'ENUM' ? getEnumValuesPayload(input.enumValues) : undefined,
    usages: getFieldUsages(input),
    filterable: true,
    section: input.section || 'Default',
    displayOrder,
    accessPolicy: input.accessPolicy,
  },
});

export const buildUpdateCustomFieldVariables = (
  fieldKey: string,
  input: CreateEmployeeFieldInput,
  displayOrder: number,
): PayrollUpdateCustomFieldRequest => ({
  fieldKey,
  input: {
    label: input.label,
    isRequired: input.isRequired,
    enumValues: input.valueType === 'ENUM' ? getEnumValuesPayload(input.enumValues) : undefined,
    usages: getFieldUsages(input),
    section: input.section || 'Default',
    displayOrder,
    accessPolicy: input.accessPolicy,
  },
});

export const getEmployeeFieldFormData = (
  initialField?: EmployeeFieldDefinition | null,
): EmployeeFieldFormData =>
  initialField
    ? {
        name: initialField.label,
        valueType: initialField.valueType,
        enumValues: initialField.enumValues ?? [],
        isRequired: initialField.isRequired,
        showOnPayslip: initialField.showOnPayslip,
        accessPolicy: initialField.accessPolicy,
      }
    : {
        name: '',
        valueType: '',
        enumValues: [],
        isRequired: false,
        showOnPayslip: false,
        accessPolicy: {},
      };

export const hasUnsavedEmployeeFieldChanges = (
  formData: Partial<EmployeeFieldFormData>,
  initialField?: EmployeeFieldDefinition | null,
): boolean => {
  const {
    name = '',
    valueType = '',
    enumValues = [],
    isRequired = false,
    showOnPayslip = false,
    accessPolicy = {},
  } = formData;

  if (initialField) {
    return (
      name !== initialField.label ||
      valueType !== initialField.valueType ||
      isRequired !== initialField.isRequired ||
      showOnPayslip !== initialField.showOnPayslip ||
      JSON.stringify(enumValues) !== JSON.stringify(initialField.enumValues ?? []) ||
      JSON.stringify(accessPolicy) !== JSON.stringify(initialField.accessPolicy)
    );
  }

  return Boolean(
    name.trim() ||
      valueType ||
      enumValues.length > 0 ||
      isRequired ||
      showOnPayslip ||
      Object.keys(accessPolicy).length > 0,
  );
};

export const employeeFieldDetailsValidationSchema: ValidationSchema<EmployeeFieldFormData> = {
  name: (formData) => (formData.name?.trim() ? null : 'Enter a field name'),
  valueType: (formData) => (formData.valueType ? null : 'Select a data type'),
  enumValues: (formData) =>
    formData.valueType === 'ENUM' && (formData.enumValues ?? []).length === 0
      ? 'Add at least one value to continue.'
      : null,
};

/**
 * Splits a committed tag-input string on commas into clean chip values:
 * trims each part, drops empties, and dedupes while keeping first occurrence.
 */
export const parseEnumValuesInput = (rawValue: string): string[] =>
  rawValue
    .split(',')
    .map((part) => part.trim())
    .filter((part, index, parts) => Boolean(part) && parts.indexOf(part) === index);

export const buildEmployeeFieldInput = (
  formData: Partial<EmployeeFieldFormData>,
  resolvedAccessPolicy: Record<number, EmployeeFieldAccess>,
  initialField?: EmployeeFieldDefinition | null,
): CreateEmployeeFieldInput => ({
  label: formData.name?.trim() ?? '',
  valueType: formData.valueType || 'STRING',
  isRequired: Boolean(formData.isRequired),
  showOnPayslip: Boolean(formData.showOnPayslip),
  enumValues: formData.valueType === 'ENUM' ? formData.enumValues ?? [] : undefined,
  section: initialField?.section || 'Default',
  accessPolicy: resolvedAccessPolicy,
});

export const formatEmployeeFieldsPaginationLabel = ({
  currentPage,
  pageSize,
  currentCount,
  total,
}: {
  currentPage: number;
  pageSize: number;
  currentCount: number;
  total: number;
}) => {
  if (total === 0) return 'Showing 0 fields';
  const start = currentPage * pageSize + 1;
  const end = Math.min(currentPage * pageSize + currentCount, total);
  return `Showing ${start}-${end} of ${total} fields`;
};
